import React, { useEffect } from 'react';
import { Typography, Switch, Tooltip } from '@mui/material';
import { styled } from '@mui/material/styles';
import InfoRoundedIcon from "@mui/icons-material/InfoRounded";
import dashboardPreferencesService from "../../api/tenantApi";
import { getCurrentUserId } from "../../authUtils";
import { toast } from 'react-toastify';



// Custom styled Switch component
const CustomSwitch = styled(Switch)(({ theme }) => ({
  width: 45,
  height: 25,
  padding: 0,
  '& .MuiSwitch-switchBase': {
    padding: 0,
    margin: 2,
    transitionDuration: '300ms',
    '&.Mui-checked': {
      transform: 'translateX(20px)',
      color: '#fff',
      '& + .MuiSwitch-track': {
        backgroundColor: '#5186EC',
        opacity: 1,
        border: 0,
      },
      '&.Mui-disabled + .MuiSwitch-track': {
        opacity: 0.5,
      },
    },
    '&.Mui-focusVisible .MuiSwitch-thumb': {
      color: '#5186EC',
      border: '6px solid #fff',
    },
  },
  '& .MuiSwitch-thumb': {
    boxSizing: 'border-box',
    width: 21,
    height: 21,
    backgroundColor: '#fff',
  },
  '& .MuiSwitch-track': {
    borderRadius: 26 / 2,
    backgroundColor: '#E9E9E9',
    opacity: 1,
    transition: theme.transitions.create(['background-color'], {
      duration: 500,
    }),
  },
}));

const NotificationSettings = ({
  isExpanded,
  notificationPreferences,
  onUpdatePreferences,
  tenantId
}) => {
  // Debug log when preferences change
  useEffect(() => {
  }, [notificationPreferences]);

  const handlePreferenceChange = async (type, channel) => {
    try {
      // Get current preferences for this channel
      const currentChannelPrefs = notificationPreferences[channel] || {};
      console.log("🚀 ~ handlePreferenceChange ~ currentChannelPrefs:", currentChannelPrefs)
      
      // Create updated preferences
      const updatedChannelPrefs = {
        Complete: type === 'Complete' ? !currentChannelPrefs.Complete : !!currentChannelPrefs.Complete,
        Update: type === 'Update' ? !currentChannelPrefs.Update : !!currentChannelPrefs.Update,
        'Status Change': type === 'Status Change' ? !currentChannelPrefs['Status Change'] : !!currentChannelPrefs['Status Change'],
        'Financial Report': type === 'Financial Report' ? !currentChannelPrefs['Financial Report'] : !!currentChannelPrefs['Financial Report'],
        'Critical Risk Alert': type === 'Critical Risk Alert' ? !currentChannelPrefs['Critical Risk Alert'] : !!currentChannelPrefs['Critical Risk Alert']
      };
    
      // Create the full updated preferences object
      const updatedPreferences = {
        ...notificationPreferences,
        [channel]: updatedChannelPrefs
      };
      console.log("🚀 ~ handlePreferenceChange ~ updatedPreferences:", updatedPreferences)
      
      // Prepare preferences string for API
      const preferencesString = JSON.stringify(updatedChannelPrefs);
      console.log("🚀 ~ handlePreferenceChange ~ preferencesString:", preferencesString)

      // Determine which feature string to use based on channel
      const featureMap = {
        inApp: "Notification Preferences",
        email: "Email Notification Preferences",
        slack: "Slack Notification Preferences",
        weekly: "Weekly Notification Preferences"
      };
      const feature = featureMap[channel];

      // Check if this is a user-specific channel (not Slack)
      const isUserSpecific = channel !== 'slack';
      
      if (isUserSpecific) {
        // Handle user-specific preferences
        const userId = getCurrentUserId();
        if (!userId) {
          toast.error('User ID not found. Please log in again.');
          return;
        }

        try {
          // Try to update user preferences first
          const updateResult = await dashboardPreferencesService.updateUserPreferencesByUserIdAndFeature(
            userId, 
            feature, 
            { preferences: preferencesString, tenantId: tenantId }
          );
          console.log('User preferences update result:', updateResult.data);
          
          if (!updateResult.data) {
            // If update returns null, create new user preferences
            const createResult = await dashboardPreferencesService.createUserPreferences({
              userId,
              feature,
              preferences: preferencesString,
              tenantId
            });
            console.log("🚀 ~ handlePreferenceChange ~ Create User Preferences Result:", createResult?.data);
          }

          // Update local state through parent component
          onUpdatePreferences(updatedPreferences);
          toast.success('User preferences updated successfully');
        } catch (error) {
          console.error('User preferences operation failed:', error.response?.data?.message);
          
          // Check if it's a conditional check failure
          if (error.response?.data?.message === 'No user preferences found for user with specified feature') {
            // Try to create new user preferences
            try {
              await dashboardPreferencesService.createUserPreferences({
                userId,
                feature,
                preferences: preferencesString,
                tenantId
              });
             
              // Update local state through parent component
              onUpdatePreferences(updatedPreferences);
              toast.success('User preferences created successfully');
            } catch (createError) {
              console.error('Failed to create user preferences:', createError);
              toast.error('Failed to create user preferences');
            }
          } else {
            toast.error('Failed to update user preferences');
          }
        }
      } else {
        // Handle tenant-specific preferences (Slack)
        try {
          // Try to update first
          const updateResult = await dashboardPreferencesService.updateDashboardPreferencesByTenantIdAndFeature(
            tenantId, 
            feature, 
            { preferences: preferencesString }
          );
          console.log('Tenant preferences update result:', updateResult.data);
          
          if (!updateResult.data) {
            // If update returns null, create new preferences
            const createResult = await dashboardPreferencesService.createDashboardPreferences({
              tenantId,
              feature,
              preferences: preferencesString
            });
            console.log("🚀 ~ handlePreferenceChange ~ Create Tenant Preferences Result:", createResult?.data);
          }

          // Update local state through parent component
          onUpdatePreferences(updatedPreferences);
          toast.success('Tenant preferences updated successfully');
        } catch (error) {
          console.error('Tenant preferences operation failed:', error.response?.data?.message);
          
          // Check if it's a conditional check failure
          if (error.response?.data?.message === 'No dashboard preferences found for tenant with specified feature') {
            // Try to create new preferences
            try {
              await dashboardPreferencesService.createDashboardPreferences({
                tenantId,
                feature,
                preferences: preferencesString
              });
             
              // Update local state through parent component
              onUpdatePreferences(updatedPreferences);
              toast.success('Tenant preferences created successfully');
            } catch (createError) {
              console.error('Failed to create tenant preferences:', createError);
              toast.error('Failed to create tenant preferences');
            }
          } else {
            toast.error('Failed to update tenant preferences');
          }
        }
      }
    } catch (error) {
      console.error('Error in preference change handler:', error);
      toast.error('Failed to update preferences');
    }
  };

  if (!isExpanded) return null;

  const notificationTypes = [
    {
      key: 'Complete',
      title: 'Notes',
      description: 'Notes are notifications that inform users on completed process'
    },
    {
      key: 'Update',
      title: 'Updates',
      description: 'Update notifications include information about new events or other type of information recently added to a company card.'
    },
    {
      key: 'Status Change',
      title: 'Status Changes',
      description: "This type of notification will alert you to any significant changes in the company's scores or credit recommendations."
    },
    {
      key: 'Financial Report',
      title: 'Financial Reports',
      description: 'This type of notification will alert you to new Financial Reports added in the company.'
    },
    {
      key: 'Critical Risk Alert',
      title: 'Critical Risk Alerts',
      description: 'Critical alerts for significant risk changes including recommendation drops (>35%).'
    },
    {
      key: 'AR Summary',
      title: 'Weekly AR Risk Summary',
      badge: 'NEW',
      description: 'A weekly email summarizing your accounts-receivable risk — customers at risk (ARC ≥ 8), exposure, and risk distribution. On by default; use the "Weekly News" column to turn it off.'
    }
  ];

  const channels = [
    {
      key: 'inApp',
      title: 'In-App',
      tooltip: "You'll get an update in the notification bar on the right-hand side of the screen."
    },
    {
      key: 'weekly',
      title: 'Weekly News',
      tooltip: 'We will send you a weekly email with all the relevant updates that happen during the week.'
    },
    {
      key: 'email',
      title: 'Email',
      tooltip: 'We will send you immediate emails. This is recommended only for status updates.'
    },
    {
      key: 'slack',
      title: 'Slack',
      tooltip: 'We will send you immediate s in slack.'
    }
  ];

  return (
    <div className="notification-settings" style={{
      background: '#FAFAFC',
      padding: '20px',
      borderRadius: '0 0 8px 8px',
      boxShadow: '0px 4px 6px -1px rgba(0, 0, 0, 0.1), 0px 2px 4px -1px rgba(0, 0, 0, 0.06), 0px 1px 3px rgba(0, 0, 0, 0.08)',
    }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th style={{ width: '30%' }}></th>
            {channels.map(channel => (
              <th key={channel.key} style={{ padding: '10px', textAlign: 'center' }}>
                {channel.title}
                <Tooltip title={channel.tooltip}>
                  <InfoRoundedIcon sx={{ fontSize: 16, marginLeft: '5px' }} />
                </Tooltip>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {notificationTypes.map(type => (
            <tr key={type.key}>
              <td style={{ padding: '10px' }}>
                <Typography variant="subtitle1" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {type.title}
                  {type.badge && (
                    <span style={{ fontSize: 9.5, fontWeight: 800, letterSpacing: '.04em', color: '#0E2A5A', background: '#FFD34E', borderRadius: 5, padding: '1px 6px', lineHeight: 1.6 }}>
                      {type.badge}
                    </span>
                  )}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {type.description}
                </Typography>
              </td>
              {channels.map(channel => {
                const isChecked = notificationPreferences[channel.key]?.[type.key] || false;
                return (
                  <td key={channel.key} style={{ textAlign: 'center' }}>
                    <CustomSwitch
                      checked={isChecked}
                      onChange={() => handlePreferenceChange(type.key, channel.key)}
                    />
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default NotificationSettings; 